import { findFactoryQuestions, parseFactoryAnswers, parseQuestionsReply, type FactoryQuestions } from './factory-questions';
import { findFactorySuggestions, parseSuggestionDecisions, suggestionsMessageRef, type FactorySuggestions, type SuggestionDecision } from './factory-suggestions';

/**
 * T-55: the software factory's rounds of questions and suggestions on a task, read from its comments,
 * so the Decisions section, the comments and the cards agree on what's open.
 *
 * - A round of questions is answered by the first Answers message or reply in words after it (the
 *   card's Send answers or Reply in words), or when the factory asks a newer round. A plain comment
 *   leaves it open, as on the factory's own board.
 * - A suggestion is decided by a decisions message (the card's Apply) after its round: the round the
 *   message names (decisionsMessageRef, when the task has more than one), else the newest before it.
 */

/** What the rounds read from a task's events. */
export interface RoundEvent {
  id: string;
  kind: string;
  body: string | null;
  created_at: string;
}

export interface QuestionRound<E extends RoundEvent = RoundEvent> {
  /** The comment that asked. */
  event: E;
  questions: FactoryQuestions;
  answered: boolean;
  /** The Answers message or reply in words, if one came. */
  reply: E | null;
  /** One answer per question from that message (null where none was given); null for a reply in words. */
  answers: (string | null)[] | null;
  /** The reply's text, when it was a reply in words rather than an answer per question. */
  words: string | null;
}

export interface SuggestionRound<E extends RoundEvent = RoundEvent> {
  event: E;
  suggestions: FactorySuggestions;
  /** Decisions so far, by suggestion number (1-based); the first one for each stands. */
  decided: Map<number, SuggestionDecision>;
  /** Every suggestion is decided. */
  settled: boolean;
  /** The decisions messages for this round. */
  replies: E[];
}

const chats = <E extends RoundEvent>(events: E[]) => events.filter((e) => e.kind === 'chat' && e.body);

/** Every round of questions in a task's events (oldest first), oldest round first. */
export function questionRounds<E extends RoundEvent>(events: E[]): QuestionRound<E>[] {
  const rounds: QuestionRound<E>[] = [];
  for (const event of chats(events)) {
    const body = event.body!;
    const found = findFactoryQuestions(body);
    if (found) {
      // A newer round: the factory has moved on from the ones still open.
      for (const r of rounds) r.answered = true;
      rounds.push({ event, questions: found.questions, answered: false, reply: null, answers: null, words: null });
      continue;
    }
    const answers = parseFactoryAnswers(body);
    const words = answers ? null : parseQuestionsReply(body);
    if (!answers && words === null) continue;
    const open = [...rounds].reverse().find((r) => !r.answered);
    if (!open) continue;
    open.answered = true;
    open.reply = event;
    open.answers = answers;
    open.words = words;
  }
  return rounds;
}

/** Every round of suggestions in a task's events (oldest first), oldest round first. */
export function suggestionRounds<E extends RoundEvent>(events: E[]): SuggestionRound<E>[] {
  const rounds: SuggestionRound<E>[] = [];
  for (const event of chats(events)) {
    const body = event.body!;
    const found = findFactorySuggestions(body);
    if (found) {
      rounds.push({ event, suggestions: found.suggestions, decided: new Map(), settled: false, replies: [] });
      continue;
    }
    const parsed = parseSuggestionDecisions(body);
    if (!parsed) continue;
    // The round it names, else the newest before it.
    const round = parsed.messageRef ? rounds.find((r) => suggestionsMessageRef(r.event.id) === parsed.messageRef) : rounds[rounds.length - 1];
    if (!round) continue;
    round.replies.push(event);
    for (const d of parsed.decisions) {
      if (d.number >= 1 && d.number <= round.suggestions.suggestions.length && !round.decided.has(d.number)) round.decided.set(d.number, d);
    }
    round.settled = round.decided.size === round.suggestions.suggestions.length;
  }
  return rounds;
}

/** What a comment is to the rounds: asks questions, makes suggestions, answers or decides them, or none. */
export type FactoryMessageKind =
  | { kind: 'questions'; count: number }
  | { kind: 'suggestions'; count: number }
  | { kind: 'answers' }
  | { kind: 'reply'; text: string }
  | { kind: 'decisions'; count: number };

export function factoryMessageKind(body: string | null): FactoryMessageKind | null {
  if (!body) return null;
  const questions = findFactoryQuestions(body);
  if (questions) return { kind: 'questions', count: questions.questions.questions.length };
  const suggestions = findFactorySuggestions(body);
  if (suggestions) return { kind: 'suggestions', count: suggestions.suggestions.suggestions.length };
  if (parseFactoryAnswers(body)) return { kind: 'answers' };
  const reply = parseQuestionsReply(body);
  if (reply !== null) return { kind: 'reply', text: reply };
  const decisions = parseSuggestionDecisions(body);
  if (decisions) return { kind: 'decisions', count: decisions.decisions.length };
  return null;
}

/** The comment's text with its questions or suggestions block taken out (what's said around it). */
export function textAroundBlock(body: string): string {
  const found = findFactoryQuestions(body) ?? findFactorySuggestions(body);
  if (!found) return body;
  const clean = body.replace(/\r\n?/g, '\n');
  return (clean.slice(0, found.start) + clean.slice(found.end)).replace(/\n{3,}/g, '\n\n').trim();
}

/** Each round's state by the comments that make it up: the asking comment and its replies, to the round's comment id and whether it's settled. */
export function roundsByMessage<E extends RoundEvent>(events: E[]): Map<string, { roundId: string; settled: boolean }> {
  const map = new Map<string, { roundId: string; settled: boolean }>();
  for (const r of questionRounds(events)) {
    const state = { roundId: r.event.id, settled: r.answered };
    map.set(r.event.id, state);
    if (r.reply) map.set(r.reply.id, state);
  }
  for (const r of suggestionRounds(events)) {
    const state = { roundId: r.event.id, settled: r.settled };
    map.set(r.event.id, state);
    for (const reply of r.replies) map.set(reply.id, state);
  }
  return map;
}

/** The ref a decisions message on `round` names: when the task has more than one suggestion round, so older ones are told apart. */
export function decisionsMessageRef<E extends RoundEvent>(rounds: SuggestionRound<E>[], round: SuggestionRound<E>): string | null {
  return rounds.length > 1 ? suggestionsMessageRef(round.event.id) : null;
}

/**
 * A comment's text where it's shown short (notifications, Slack, previews): the factory's questions
 * or suggestions as a line (with what's said around the block), never the block's JSON; answers and
 * decisions as a line too. Any other comment as it is.
 */
export function factoryExcerpt(body: string): string {
  const kind = factoryMessageKind(body);
  if (!kind) return body;
  const plain = (s: string) => s.replace(/\*\*/g, '');
  switch (kind.kind) {
    case 'questions':
    case 'suggestions': {
      const line =
        kind.kind === 'questions'
          ? kind.count === 1
            ? 'Asked a question'
            : `Asked ${kind.count} questions`
          : kind.count === 1
            ? 'Suggested a further change'
            : `Suggested ${kind.count} further changes`;
      const around = textAroundBlock(body);
      return around ? `${line}: ${around}` : line;
    }
    case 'reply':
      return `Replied to the questions: ${kind.text}`;
    case 'answers':
    case 'decisions':
      // "Answers 1. Scope: … → Yes", without the bold marks.
      return plain(body);
  }
}
